import { Json } from '../../supabase/types/database.types';

export type SceneGateRollEntry = {
  name: string;
  definition: string;
  characterId: number;
  description: string;
};

export type ChoiceRollSpec = {
  name: string;
  description: string;
};

export type SuggestedActionEntry = {
  action: string;
  allowedUser?: string[] | null;
  data?: unknown | null;
};

export const KNOWN_SUGGESTED_ACTIONS = ['createCharacter', 'restShort', 'restLong'] as const;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Stato scena plugin (specchio per-turno in `Messages.extra_data.sceneState`;
 * la fonte di verità UI è la colonna `Chats.plugin_scene_state`).
 * Forma kernel minima; il payload è opaco/plugin-defined.
 */
export type SceneStateMirror = {
  mode: string;
  label?: string | null;
  payload?: Record<string, unknown> | null;
};

export function parseSceneState(raw: unknown): SceneStateMirror | null {
  if (!isRecord(raw)) return null;
  const mode = raw['mode'];
  if (typeof mode !== 'string' || mode.trim().length === 0) return null;
  const label = raw['label'];
  const payload = raw['payload'];
  return {
    mode: mode.trim(),
    ...(typeof label === 'string' && label.trim() ? { label: label.trim() } : {}),
    ...(isRecord(payload) ? { payload } : {}),
  };
}

/** Variante per `Messages.extra_data` (chiave `sceneState`). */
export type CombatResult = 'pc_dying' | 'pc_stable' | 'pc_dead' | 'combat_ended';

export function parseCombatResult(
  extra_data: Json | null | undefined,
): CombatResult | null {
  const state = parseSceneStateExtra(extra_data);
  const raw = state?.payload?.['combatResult'];
  if (
    raw === 'pc_dying' ||
    raw === 'pc_stable' ||
    raw === 'pc_dead' ||
    raw === 'combat_ended'
  ) {
    return raw;
  }
  return null;
}

export type NarrativeOverride = {
  reason: string;
  description: string;
};

export function parseNarrativeOverride(
  extra_data: Json | null | undefined,
): NarrativeOverride | null {
  if (!isRecord(extra_data)) return null;
  const raw = extra_data['narrativeOverride'];
  if (!isRecord(raw)) return null;
  const reason = raw['reason'];
  const description = raw['description'];
  if (typeof reason !== 'string' || typeof description !== 'string') return null;
  return { reason, description };
}

export function parseSceneStateExtra(
  extra_data: Json | null | undefined,
): SceneStateMirror | null {
  if (extra_data === null || extra_data === undefined) return null;
  if (!isRecord(extra_data)) return null;
  return parseSceneState(extra_data['sceneState']);
}

function parseSceneGateRollEntry(raw: unknown): SceneGateRollEntry | null {
  if (!isRecord(raw)) return null;
  const name = raw['name'];
  const definition = raw['definition'];
  const characterId = raw['characterId'];
  const description = raw['description'];
  if (typeof name !== 'string' || name.length === 0) return null;
  if (typeof definition !== 'string' || definition.length === 0) return null;
  if (typeof characterId !== 'number' || !Number.isInteger(characterId) || characterId <= 0)
    return null;
  if (typeof description !== 'string') return null;
  return { name, definition, characterId, description };
}

export function parseSceneGateRolls(extra_data: Json | null | undefined): SceneGateRollEntry[] {
  if (extra_data === null || extra_data === undefined) return [];
  if (!isRecord(extra_data)) return [];
  const rolls = extra_data['sceneGateRolls'];
  if (!Array.isArray(rolls)) return [];
  const out: SceneGateRollEntry[] = [];
  for (const item of rolls) {
    const e = parseSceneGateRollEntry(item);
    if (e) out.push(e);
  }
  return out;
}

export type SuggestedMessageChoice = {
  message: string;
  label?: string | null;
  choiceRoll?: ChoiceRollSpec;
};

export type SuggestedMessageEntry = {
  characterId: number;
  choices: SuggestedMessageChoice[];
};

const knownActionsSet: ReadonlySet<string> = new Set(KNOWN_SUGGESTED_ACTIONS);

function parseSuggestedActionEntry(raw: unknown): SuggestedActionEntry | null {
  if (!isRecord(raw)) return null;
  const action = raw['action'];
  if (typeof action !== 'string' || action.length === 0) return null;
  if (!knownActionsSet.has(action)) return null;

  const allowedUser = raw['allowedUser'];
  let parsedAllowedUser: string[] | null | undefined;
  if (allowedUser === null || allowedUser === undefined) {
    parsedAllowedUser = allowedUser as null | undefined;
  } else if (Array.isArray(allowedUser)) {
    parsedAllowedUser = allowedUser.filter((u): u is string => typeof u === 'string');
  } else {
    return null;
  }

  return { action, allowedUser: parsedAllowedUser, data: raw['data'] ?? null };
}

export function parseSuggestedActions(extra_data: Json | null | undefined): SuggestedActionEntry[] {
  if (extra_data === null || extra_data === undefined) return [];
  if (!isRecord(extra_data)) return [];
  const sa = extra_data['suggestedActions'];
  if (!Array.isArray(sa)) return [];
  const out: SuggestedActionEntry[] = [];
  for (const item of sa) {
    const e = parseSuggestedActionEntry(item);
    if (e) out.push(e);
  }
  return out;
}

function parseChoiceRoll(raw: unknown): ChoiceRollSpec | null {
  if (!isRecord(raw)) return null;
  const name = raw['name'];
  const description = raw['description'];
  if (typeof name !== 'string' || name.trim().length === 0) return null;
  if (typeof description !== 'string') return null;
  return { name: name.trim(), description: description.trim() };
}

/** Legge choiceRoll o, in lettura, il legacy requestedOperations[0] / requestedOperation. */
function parseChoiceRollField(raw: Record<string, unknown>): ChoiceRollSpec | undefined {
  const direct = parseChoiceRoll(raw['choiceRoll']);
  if (direct) return direct;

  const legacyArray = raw['requestedOperations'];
  if (legacyArray != null) {
    const fromArray = parseChoiceRoll(Array.isArray(legacyArray) ? legacyArray[0] : legacyArray);
    if (fromArray) return fromArray;
  }

  const legacySingle = raw['requestedOperation'];
  if (legacySingle != null) {
    const fromSingle = parseChoiceRoll(legacySingle);
    if (fromSingle) return fromSingle;
  }

  return undefined;
}

function parseSuggestedMessageChoice(raw: unknown): SuggestedMessageChoice | null {
  if (!isRecord(raw)) return null;
  const message = raw['message'];
  if (typeof message !== 'string' || message.trim().length === 0) return null;
  const label = raw['label'];
  const parsedLabel =
    label === null || label === undefined
      ? null
      : typeof label === 'string' && label.trim().length > 0
        ? label.trim()
        : null;

  const choiceRoll = parseChoiceRollField(raw);

  return {
    message: message.trim(),
    label: parsedLabel,
    ...(choiceRoll ? { choiceRoll } : {}),
  };
}

function parseSuggestedMessageEntry(raw: unknown): SuggestedMessageEntry | null {
  if (!isRecord(raw)) return null;
  const characterId = raw['characterId'];
  if (typeof characterId !== 'number' || !Number.isInteger(characterId) || characterId <= 0)
    return null;
  if (!Array.isArray(raw['choices'])) return null;
  const choices: SuggestedMessageChoice[] = [];
  for (const item of raw['choices']) {
    const c = parseSuggestedMessageChoice(item);
    if (c) choices.push(c);
  }
  if (choices.length === 0) return null;
  return { characterId, choices };
}

export type SheetUpdateRecapAppliedRow = {
  path: string[];
  label: string;
  before: string;
  after: string;
};

export type SheetUpdateRecapSkippedRow = {
  path: string[];
  label: string;
  reason: string;
};

export type SheetUpdateRecapEntry = {
  characterId: number;
  characterName: string;
  applied: SheetUpdateRecapAppliedRow[];
  skipped?: SheetUpdateRecapSkippedRow[];
};

function parseSheetUpdateRecapAppliedRow(raw: unknown): SheetUpdateRecapAppliedRow | null {
  if (!isRecord(raw)) return null;
  const path = raw['path'];
  const label = raw['label'];
  const before = raw['before'];
  const after = raw['after'];
  if (!Array.isArray(path) || !path.every((s) => typeof s === 'string')) return null;
  if (typeof label !== 'string' || typeof before !== 'string' || typeof after !== 'string') {
    return null;
  }
  return { path, label, before, after };
}

function parseSheetUpdateRecapSkippedRow(raw: unknown): SheetUpdateRecapSkippedRow | null {
  if (!isRecord(raw)) return null;
  const path = raw['path'];
  const label = raw['label'];
  const reason = raw['reason'];
  if (!Array.isArray(path) || !path.every((s) => typeof s === 'string')) return null;
  if (typeof label !== 'string' || typeof reason !== 'string') return null;
  return { path, label, reason };
}

function parseSheetUpdateRecapEntry(raw: unknown): SheetUpdateRecapEntry | null {
  if (!isRecord(raw)) return null;
  const characterId = raw['characterId'];
  const characterName = raw['characterName'];
  if (typeof characterId !== 'number' || !Number.isInteger(characterId) || characterId <= 0) {
    return null;
  }
  if (typeof characterName !== 'string' || characterName.length === 0) return null;
  if (!Array.isArray(raw['applied'])) return null;
  const applied: SheetUpdateRecapAppliedRow[] = [];
  for (const row of raw['applied']) {
    const p = parseSheetUpdateRecapAppliedRow(row);
    if (p) applied.push(p);
  }
  if (applied.length === 0) return null;

  const skippedRaw = raw['skipped'];
  let skipped: SheetUpdateRecapSkippedRow[] | undefined;
  if (Array.isArray(skippedRaw)) {
    const parsed: SheetUpdateRecapSkippedRow[] = [];
    for (const row of skippedRaw) {
      const s = parseSheetUpdateRecapSkippedRow(row);
      if (s) parsed.push(s);
    }
    if (parsed.length) skipped = parsed;
  }

  return { characterId, characterName, applied, ...(skipped ? { skipped } : {}) };
}

export function parseSheetUpdateRecap(
  extra_data: Json | null | undefined,
): SheetUpdateRecapEntry[] {
  if (extra_data === null || extra_data === undefined) return [];
  if (!isRecord(extra_data)) return [];
  const recap = extra_data['sheetUpdateRecap'];
  if (!Array.isArray(recap)) return [];
  const out: SheetUpdateRecapEntry[] = [];
  for (const item of recap) {
    const e = parseSheetUpdateRecapEntry(item);
    if (e) out.push(e);
  }
  return out;
}

export function parseSuggestedMessages(extra_data: Json | null | undefined): SuggestedMessageEntry[] {
  if (extra_data === null || extra_data === undefined) return [];
  if (!isRecord(extra_data)) return [];
  const sm = extra_data['suggestedMessage'];
  if (!Array.isArray(sm)) return [];
  const out: SuggestedMessageEntry[] = [];
  for (const item of sm) {
    const e = parseSuggestedMessageEntry(item);
    if (e) out.push(e);
  }
  return out;
}

export type LoreCorrectionProposal = {
  id: string;
  type: 'message' | 'graph' | 'story' | 'chapter';
  targetMessageId?: number;
  targetChatId?: number;
  proposedText?: string;
  graphNotions?: string;
  newStoryName?: string;
  newChapterTitle?: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
};

function parseCorrectionProposal(raw: unknown): LoreCorrectionProposal | null {
  if (!isRecord(raw)) return null;
  const id = raw['id'];
  const type = raw['type'];
  const reason = raw['reason'];
  const status = raw['status'];
  if (typeof id !== 'string' || typeof reason !== 'string') return null;
  if (
    type !== 'message' &&
    type !== 'graph' &&
    type !== 'story' &&
    type !== 'chapter'
  ) {
    return null;
  }
  if (
    status !== 'pending' &&
    status !== 'approved' &&
    status !== 'rejected'
  ) {
    return null;
  }
  return {
    id,
    type,
    reason,
    status,
    ...(typeof raw['targetMessageId'] === 'number'
      ? { targetMessageId: raw['targetMessageId'] }
      : {}),
    ...(typeof raw['targetChatId'] === 'number'
      ? { targetChatId: raw['targetChatId'] }
      : {}),
    ...(typeof raw['proposedText'] === 'string'
      ? { proposedText: raw['proposedText'] }
      : {}),
    ...(typeof raw['graphNotions'] === 'string'
      ? { graphNotions: raw['graphNotions'] }
      : {}),
    ...(typeof raw['newStoryName'] === 'string'
      ? { newStoryName: raw['newStoryName'] }
      : {}),
    ...(typeof raw['newChapterTitle'] === 'string'
      ? { newChapterTitle: raw['newChapterTitle'] }
      : {}),
  };
}

export function parseCorrectionProposals(
  extra_data: Json | null | undefined,
): LoreCorrectionProposal[] {
  if (extra_data === null || extra_data === undefined) return [];
  if (!isRecord(extra_data)) return [];
  const raw = extra_data['correctionProposals'];
  if (!Array.isArray(raw)) return [];
  const out: LoreCorrectionProposal[] = [];
  for (const item of raw) {
    const e = parseCorrectionProposal(item);
    if (e) out.push(e);
  }
  return out;
}
